import type { ScheduledEventRecurringSettings } from "@package/ui-gql/index-data-access";
import moment from "moment";
import { RRule, type Options } from "rrule";

export function buildRRuleFromSettings(
  recurring: ScheduledEventRecurringSettings | null | undefined,
  fallbackStart: Date,
): RRule | null {
  if (!recurring) {
    return null;
  }

  const options: Partial<Options> = {
    freq: recurring.freq ?? RRule.WEEKLY,
    interval: recurring.interval ?? 1,
    dtstart: recurring.dtstart
      ? moment(recurring.dtstart).toDate()
      : fallbackStart,
    until: recurring.until ? moment(recurring.until).toDate() : undefined,
    count: recurring.count ?? undefined,
    byweekday: recurring.byweekday ?? undefined,
    bymonth: recurring.bymonth ?? undefined,
    tzid: recurring.tzid ?? undefined,
  };

  return new RRule(options);
}

export function summarizeRecurrence(
  recurring: ScheduledEventRecurringSettings | null | undefined,
  fallbackStart: Date,
): string | null {
  const rrule = buildRRuleFromSettings(recurring, fallbackStart);

  return rrule ? rrule.toText() : null;
}

/**
 * Pulls only the first `count` occurrences via rrule's iterator callback
 * instead of `.all().slice(0, count)` -- an unbounded or 1000-occurrence
 * rule would otherwise be fully materialized just to preview five dates.
 */
export function nextOccurrences(
  recurring: ScheduledEventRecurringSettings | null | undefined,
  fallbackStart: Date,
  count = 5,
): Date[] {
  const rrule = buildRRuleFromSettings(recurring, fallbackStart);

  if (!rrule) {
    return [];
  }

  return rrule.all((_date, index) => index < count);
}
